import React, { useEffect, useState } from "react";
import { Modal, ModalBody } from "react-bootstrap";
import ModalHeader from "react-bootstrap/esm/ModalHeader";
import { useDispatch, useSelector } from "react-redux";
import { approveCenter } from "../../../../store/actions/councilActions/centerActions";
import { getTrades } from "../../../../store/actions/councilActions/trade";
import { getBlogCategories } from "../../../../store/actions/councilActions/blogAction";

/*
  Approve a center.

  Besides the code / password / payment method, the council now ticks WHICH
  courses (main trade categories) the center is approved for and sets the seat
  intake for each. Only these courses show on the center's dashboard and on its
  affiliation certificate. `approved` (from the center detail) pre-ticks them
  when a center is being re-approved.
*/
const ApproveCenter = ({ show, onHide, id, approved, appliedTrades, blogAccess, blogCategories }) => {
  const dispatch = useDispatch();
  const { admin } = useSelector((state) => state);
  const { trades } = admin;

  // The trades the centre APPLIED for (center.center_trade, comma list). Every
  // trade is shown, but the admin can only tick/untick the ones the centre
  // applied for. (Empty = a legacy centre with no applied list -> allow all.)
  const appliedSet = new Set(
    (appliedTrades ? String(appliedTrades).split(",") : [])
      .map((x) => x.trim())
      .filter(Boolean)
  );
  const isApplied = (tid) => appliedSet.size === 0 || appliedSet.has(String(tid));

  const [data, setData] = useState({
    total_seats: "",
    center_code: "",
    password: "",
    id: id,
    payment_mode: "",
  });
  // { [trade_id]: { checked, seats } }
  const [courses, setCourses] = useState({});

  // Blog posting: whether this centre may post, and which categories it may
  // post in. blogCats is a set of allowed blog_category ids.
  const [blogAllow, setBlogAllow] = useState(false);
  const [blogCatList, setBlogCatList] = useState([]);
  const [blogCats, setBlogCats] = useState({});

  const { total_seats, center_code, password, payment_mode } = data;

  useEffect(() => {
    if (!trades) dispatch(getTrades());
    dispatch(getBlogCategories(false)).then((list) => setBlogCatList(list || []));
  }, [dispatch, trades]);

  // pre-tick already-approved courses when re-approving
  useEffect(() => {
    if (Array.isArray(approved) && approved.length) {
      const pre = {};
      approved.forEach((a) => {
        pre[a.trade_id] = { checked: true, seats: a.seats };
      });
      setCourses(pre);
    }
  }, [approved]);

  // pre-fill blog access + allowed categories
  useEffect(() => {
    setBlogAllow(Number(blogAccess) === 1);
    const pre = {};
    (Array.isArray(blogCategories) ? blogCategories : []).forEach((cid) => {
      pre[cid] = true;
    });
    setBlogCats(pre);
  }, [blogAccess, blogCategories]);

  const handleChange = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  const toggleCourse = (tid) => {
    if (!isApplied(tid)) return; // admin can only approve trades the centre applied for
    setCourses((c) => ({
      ...c,
      [tid]: { checked: !c[tid]?.checked, seats: c[tid]?.seats ?? "" },
    }));
  };

  const setSeats = (tid, seats) => {
    setCourses((c) => ({ ...c, [tid]: { checked: true, seats } }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const chosen = Object.keys(courses)
      .filter((tid) => courses[tid]?.checked && isApplied(tid))
      .map((tid) => ({ trade_id: Number(tid), seats: Number(courses[tid].seats) || 0 }));

    const allowedCats = Object.keys(blogCats)
      .filter((cid) => blogCats[cid])
      .map((cid) => Number(cid));

    const response = await dispatch(
      approveCenter(
        {
          ...data,
          courses: chosen,
          blog_access: blogAllow ? 1 : 0,
          blog_categories: allowedCats,
        },
        id
      )
    );
    if (response) onHide();
  };

  return (
    <div>
      <Modal show={show} onHide={onHide} centered size="lg">
        <ModalHeader>Approve Center</ModalHeader>
        <ModalBody>
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-6 form-group">
                <label>
                  Center Code <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter Center Code"
                  name="center_code"
                  value={center_code}
                  required
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-6 form-group">
                <label>
                  Center Password <span className="text-danger">*</span>
                </label>
                <input
                  type="password"
                  className="form-control"
                  placeholder="Enter Center Password"
                  name="password"
                  value={password}
                  required
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-6 form-group">
                <label>
                  Total No of Seats <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="Enter Number of Seats"
                  name="total_seats"
                  value={total_seats}
                  required
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-6 form-group">
                <label>
                  Payment Method <span className="text-danger">*</span>
                </label>
                <select
                  name={"payment_mode"}
                  className="form-control"
                  value={payment_mode}
                  onChange={handleChange}
                  required
                >
                  <option value={""}>--Select--</option>
                  <option value={"online"}>Online</option>
                  <option value="offline">offline</option>
                  <option value="both">Both (Online/Offline)</option>
                </select>
              </div>
            </div>

            {/* approved courses (main trade categories) + per-course seats */}
            <div className="form-group">
              <label>
                Approved Courses &amp; Seats <span className="text-danger">*</span>
              </label>
              <div
                style={{
                  maxHeight: "230px",
                  overflowY: "auto",
                  border: "1px solid #e0e0e0",
                  borderRadius: "6px",
                  padding: "8px",
                }}
              >
                {!trades ? (
                  <div>Loading courses...</div>
                ) : trades.length === 0 ? (
                  <div>No course found.</div>
                ) : (
                  trades.map((t) => {
                    const applied = isApplied(t.id);
                    return (
                      <div
                        className="d-flex align-items-center justify-content-between"
                        key={t.id}
                        style={{
                          padding: "4px 2px",
                          borderBottom: "1px solid #f2f2f2",
                          opacity: applied ? 1 : 0.55,
                        }}
                      >
                        <label
                          className="mb-0"
                          style={{
                            flex: 1,
                            cursor: applied ? "pointer" : "not-allowed",
                            color: applied ? "#0a7d2c" : "#9ca3af",
                            fontWeight: applied ? 600 : 400,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={!!courses[t.id]?.checked}
                            disabled={!applied}
                            onChange={() => toggleCourse(t.id)}
                          />{" "}
                          {t.name}
                          {!applied && (
                            <span style={{ fontSize: 11, color: "#9ca3af" }}>
                              {" "}
                              (not applied)
                            </span>
                          )}
                        </label>
                        <input
                          type="number"
                          min="0"
                          className="form-control"
                          style={{ width: "110px" }}
                          placeholder="Seats"
                          value={courses[t.id]?.seats ?? ""}
                          disabled={!applied || !courses[t.id]?.checked}
                          onChange={(e) => setSeats(t.id, e.target.value)}
                        />
                      </div>
                    );
                  })
                )}
              </div>
              <small className="text-muted">
                Only the trades this centre applied for can be ticked (green);
                the rest are shown but disabled. Tick the ones to approve and set
                seats for each.
              </small>
            </div>

            {/* blog posting rights for this centre */}
            <div className="form-group">
              <label className="mb-1" style={{ cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={blogAllow}
                  onChange={() => setBlogAllow((v) => !v)}
                />{" "}
                Allow Blog Posting
              </label>
              {blogAllow && (
                <div
                  style={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "6px",
                    padding: "8px",
                    maxHeight: "180px",
                    overflowY: "auto",
                  }}
                >
                  <small className="text-muted d-block mb-1">
                    Categories this centre may post in:
                  </small>
                  {blogCatList.length === 0 ? (
                    <div>No blog categories found.</div>
                  ) : (
                    blogCatList.map((c) => (
                      <label
                        key={c.id}
                        className="mb-0 d-block"
                        style={{ cursor: "pointer", padding: "2px 0" }}
                      >
                        <input
                          type="checkbox"
                          checked={!!blogCats[c.id]}
                          onChange={() =>
                            setBlogCats((s) => ({ ...s, [c.id]: !s[c.id] }))
                          }
                        />{" "}
                        {c.name}
                      </label>
                    ))
                  )}
                </div>
              )}
            </div>

            <button type="submit" className="btn btn-primary">
              Upload
            </button>
          </form>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default ApproveCenter;
